/**
 * Балансы по биржам — GET /api/balances.
 *
 * Живёт в портфеле, а не в нижних вкладках терминала: в терминале смотрят на
 * стакан и спред, а «сколько у меня денег» — вопрос портфеля.
 *
 * Ключевое здесь — метка счёта. На binance и hyperliquid спот и деривативы
 * это ДВА РАЗНЫХ кошелька с разными деньгами; пока показывали только
 * фьючерсный, спотовые тысячи выглядели пропавшими. Поэтому строки не
 * склеиваем, а показываем обе и подводим итог по бирже.
 *
 * 16.10 — в стиле терминала: биржа — карточка (логотип, итог, доля от всех
 * денег полоской), внутри строки счетов со свободной долей. Расчёт и фильтры
 * те же, что были в таблице.
 */
import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { KeyRound, Wallet, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import { startPolling } from '../utils/poll';
import { ExchangeIcon, exchangeLabel } from './ExchangeIcon';
import type { BalanceRow } from '../types';
import { fmtNum, fmtUsd } from '../utils/money';

/** Монеты, которые считаем за доллар при подведении итога. Курсов у панели нет,
 *  и складывать BTC с USDT она права не имеет — такие строки в сумму не идут. */
const STABLES = new Set(['USDT', 'USDC', 'USD', 'DAI', 'FDUSD', 'TUSD', 'BUSD']);

/** Ниже этого порога строка не показывается — это пыль, а не деньги. */
const MIN_SHOW_USD = 1;

const isStable = (asset: string) => STABLES.has((asset || '').toUpperCase());

/** Сумма долларовых строк набора. Нестабильные монеты игнорируются осознанно. */
const sumUsd = (rows: BalanceRow[]) =>
  rows.reduce((s, r) => (isStable(r.asset) ? s + (Number(r.total) || 0) : s), 0);
/** То же по свободной части — для сводки на странице балансов. */
const sumFreeUsd = (rows: BalanceRow[]) =>
  rows.reduce((s, r) => (isStable(r.asset) ? s + (Number(r.available) || 0) : s), 0);

// 29.09: формат общий с терминалом (utils/money). Раньше toLocaleString(undefined)
// давал «6 676,6405» / «$1 037,09» — по локали браузера и с 4 знаками у долларов.
// Стейблы — 2 знака; у монет (режим «все активы») оставляем 4, там это количество.
const fmt = (v: number | null | undefined, frac = 2): string => fmtNum(v, frac);
const amount = (v: number, asset: string) => fmt(v, isStable(asset) ? 2 : 4);

const usd = (v: number) => fmtUsd(v);

/** Стили панели: карточки бирж, полоски доли, скелет загрузки. Появление —
 *  только при монтировании карточки; на опросе раз в 10 с ничего не мигает. */
const BAL_CSS = `
.bal-grid{display:grid;grid-template-columns:minmax(0,1fr);gap:10px;align-items:start}
@media (min-width:640px){.bal-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (min-width:1280px){.bal-grid{grid-template-columns:repeat(3,minmax(0,1fr))}}
.bal-card{border-radius:12px;border:1px solid hsl(var(--border));
  background:linear-gradient(180deg,hsl(var(--foreground) / .035),hsl(var(--foreground) / .012));
  transition:border-color .2s,transform .2s,box-shadow .2s;animation:bal-in .4s cubic-bezier(.2,.8,.2,1) both}
.bal-card:hover{border-color:hsl(var(--primary) / .45);transform:translateY(-1px);box-shadow:0 10px 24px -16px hsl(var(--primary) / .7)}
@keyframes bal-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.bal-share{height:4px;border-radius:999px;background:hsl(var(--foreground) / .07);overflow:hidden}
.bal-share>i{display:block;height:100%;border-radius:999px;background:linear-gradient(90deg,hsl(var(--primary) / .55),hsl(var(--primary)))}
.bal-free{height:3px;border-radius:999px;background:hsl(var(--foreground) / .06);overflow:hidden}
.bal-free>i{display:block;height:100%;border-radius:999px;background:hsl(var(--trade-green) / .7)}
.bal-row{border-top:1px solid hsl(var(--border) / .5);transition:background-color .15s}
.bal-row:hover{background:hsl(var(--foreground) / .035)}
.bal-skel{border-radius:8px;background:linear-gradient(90deg,hsl(var(--foreground) / .05),hsl(var(--foreground) / .1),hsl(var(--foreground) / .05));
  background-size:200% 100%;animation:bal-skel 1.3s ease-in-out infinite}
@keyframes bal-skel{from{background-position:100% 0}to{background-position:-100% 0}}
:root:not(.dark) .bal-card{background:hsl(var(--card))}
@media (prefers-reduced-motion:reduce){.bal-card,.bal-skel{animation:none !important}.bal-card,.bal-row{transition:none !important}}
`;

/** Метка счёта. Спот выделяем — именно его раньше не было видно. */
const AccountTag: React.FC<{ account?: string }> = ({ account }) => {
  const { t } = useTranslation('balancesPanel');
  const label = account === 'spot' ? t('account.spot')
    : account === 'earn' ? 'earn'
    // Единый счёт (bitget UTA): кошелёк один на спот и деривативы, делить
    // нечего — но и звать его «фьючерсами» неправильно.
    : account === 'unified' ? t('account.unified')
    : t('account.futures');
  const cls = account === 'spot' ? 'bg-primary/15 text-primary'
    // Earn отдельным цветом: этими деньгами торговать нельзя, пока не переведёшь
    // их из накоплений на торговый счёт.
    : account === 'earn' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
    : 'bg-foreground/[0.07] text-muted-foreground';
  return (
    <span className={clsx('inline-flex h-5 items-center rounded-md px-1.5 font-mono text-[10px] uppercase tracking-wide', cls)}
          title={account === 'earn' ? t('account.earnTitle') : undefined}>
      {label}
    </span>
  );
};

/** Сводка для шапки страницы балансов: всего денег, свободно, бирж, крупнейшая. */
export interface BalancesSummary {
  total: number;
  free: number;
  venues: number;
  top: { exchange: string; total: number } | null;
}

export interface BalancesPanelProps {
  /** Показывать и не-стейблы (монеты спот-ноги, остатки). По умолчанию —
   *  только деньги: владелец про пыль сказал прямо. */
  allAssets?: boolean;
  /** Любое изменение значения — перечитать балансы (после ручного опроса). */
  refreshToken?: number;
  /** Момент последнего опроса бирж (мс) — страница показывает возраст цифр. */
  onLoaded?: (ts: number | null) => void;
  /** Сводка наверх (страница рисует её плитками) — тогда строку «Всего» панель
   *  не печатает. */
  onSummary?: (s: BalancesSummary | null) => void;
}

export const BalancesPanel: React.FC<BalancesPanelProps> = ({ allAssets = false, refreshToken = 0, onLoaded, onSummary }) => {
  const { t } = useTranslation('balancesPanel');
  const [rows, setRows] = useState<BalanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await apiClient.raw.get('/api/balances');
        const byEx: Record<string, Array<Omit<BalanceRow, 'exchange'>>> = r.data?.balances || {};
        const out: BalanceRow[] = [];
        for (const [ex, list] of Object.entries(byEx)) {
          for (const b of list || []) out.push({ exchange: ex, ...b });
        }
        if (alive) { setRows(out); setErr(null); onLoaded?.(Number(r.data?.ts) || null); }
      } catch (e: any) {
        if (alive) setErr(e?.response?.data?.detail || e?.message || String(e));
      } finally { if (alive) setLoading(false); }
    };
    // Без наложения запросов и без опроса на скрытой вкладке.
    const stop = startPolling(load, 10_000);
    return () => { alive = false; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // Группируем по бирже: у одной площадки несколько строк (спот +
  // фьючерсы), и без группировки итог по ней приходилось складывать глазами.
  const groups = useMemo(() => {
    const byEx = new Map<string, BalanceRow[]>();
    // Показываем только деньги. Спотовый кошелёк отдаёт всё подряд — пыль от
    // старых сделок, LD-токены стейкинга, обломки airdrop'ов, — и таблица из
    // десяти строк с нулями мешает увидеть главное. Торгуем мы в стейблах,
    // остальное к торговому балансу отношения не имеет.
    for (const r of rows) {
      const stable = isStable(r.asset);
      if (!stable && !allAssets) continue;
      // Мельче доллара — это пыль от старых сделок: место занимает, смысла
      // не несёт. Владелец про такие строки сказал прямо. Для монет курса у
      // панели нет — режем только нулевые остатки.
      if (stable ? (Number(r.total) || 0) < MIN_SHOW_USD : !((Number(r.total) || 0) > 0)) continue;
      const list = byEx.get(r.exchange);
      if (list) list.push(r); else byEx.set(r.exchange, [r]);
    }
    return [...byEx.entries()]
      .map(([exchange, list]) => ({
        exchange,
        // Внутри биржи: сначала фьючерсы, потом спот; деньги выше монет;
        // крупные суммы выше.
        rows: [...list].sort((a, b) =>
          (a.account === 'spot' ? 1 : 0) - (b.account === 'spot' ? 1 : 0)
          || (isStable(b.asset) ? 1 : 0) - (isStable(a.asset) ? 1 : 0)
          || (Number(b.total) || 0) - (Number(a.total) || 0)),
        total: sumUsd(list),
        hasMoney: list.some(r => isStable(r.asset)),
      }))
      .sort((a, b) => b.total - a.total);
  }, [rows, allAssets]);

  const grandTotal = useMemo(() => sumUsd(rows), [rows]);

  // Сводка наверх: только когда данные есть, иначе плитки показывают «—».
  const summaryKey = `${grandTotal}|${groups.length}|${groups[0]?.exchange ?? ''}|${groups[0]?.total ?? 0}|${rows.length}`;
  useEffect(() => {
    if (!onSummary) return;
    if (!rows.length) { onSummary(null); return; }
    onSummary({
      total: grandTotal,
      free: sumFreeUsd(rows),
      venues: groups.length,
      top: groups[0] && groups[0].total > 0 ? { exchange: groups[0].exchange, total: groups[0].total } : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryKey]);

  if (loading && rows.length === 0) {
    return (
      <div className="bal-grid" aria-busy="true">
        <style>{BAL_CSS}</style>
        {[0, 1, 2].map(i => (
          <div key={i} className="bal-card space-y-3 p-3.5" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-center gap-2"><div className="bal-skel size-6" /><div className="bal-skel h-4 w-24" /><div className="bal-skel ml-auto h-5 w-20" /></div>
            <div className="bal-skel h-1" />
            <div className="bal-skel h-8" />
          </div>
        ))}
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }
  if (err) {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-trade-red/40 bg-trade-red/10 px-3 py-2.5 text-[13px] text-trade-red">
        <AlertCircle size={15} className="mt-0.5 shrink-0" /> <span className="break-words">{err}</span>
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-muted-foreground">
        <span className="sb-badgeicon !bg-foreground/5 !text-muted-foreground !shadow-none"><Wallet size={16} /></span>
        <span className="text-[14px]">{t('emptyTitle')}</span>
        <Link to="/api-keys"
              className="sb-press mt-1 inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-border px-3 text-[13px] text-foreground hover:border-primary/50">
          <KeyRound size={13} /> {t('emptyAction')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <style>{BAL_CSS}</style>
      {!onSummary && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-[12px] text-muted-foreground">{t('grandTotal')}</span>
          <span className="font-mono text-lg font-semibold tabular-nums text-trade-green">{usd(grandTotal)}</span>
          <span className="text-[12px] text-muted-foreground">{t('venues', { count: groups.length })}</span>
        </div>
      )}

      <div className="bal-grid">
        {groups.map((g, gi) => {
          const share = grandTotal > 0 ? Math.min(100, Math.max(0, (g.total / grandTotal) * 100)) : 0;
          return (
            <div key={g.exchange} className="bal-card overflow-hidden" style={{ animationDelay: `${Math.min(gi, 8) * 45}ms` }}>
              <div className="px-3.5 pb-2.5 pt-3">
                <div className="flex items-center gap-2">
                  <ExchangeIcon ex={g.exchange} size={20} decorative />
                  <span className="min-w-0 flex-1 truncate text-[14px] font-medium">{exchangeLabel(g.exchange)}</span>
                  {g.hasMoney && (
                    <span className="font-mono text-[15px] font-semibold tabular-nums"
                          title={g.rows.length > 1 ? t('exchangeTotal', { exchange: exchangeLabel(g.exchange) }) : undefined}>
                      {usd(g.total)}
                    </span>
                  )}
                </div>
                {g.hasMoney && grandTotal > 0 && (
                  <div className="mt-2 flex items-center gap-2" title={t('shareTitle')}>
                    <div className="bal-share flex-1"><i style={{ width: `${share}%` }} /></div>
                    <span className="w-9 text-right font-mono text-[11px] tabular-nums text-muted-foreground">{share.toFixed(0)}%</span>
                  </div>
                )}
              </div>
              <div>
                {g.rows.map(r => {
                  const total = Number(r.total) || 0;
                  const avail = Number(r.available) || 0;
                  // Больше 100% быть не может: если биржа отдала «доступно»
                  // больше собственного капитала (в поле лежит плечо, а не
                  // свои деньги), показываем 100 — иначе цифра врёт.
                  const freePct = total > 0 ? Math.min(100, Math.max(0, (avail / total) * 100)) : null;
                  return (
                    <div key={`${r.account ?? ''}|${r.asset}`} className="bal-row px-3.5 py-2">
                      <div className="flex items-center gap-2">
                        <AccountTag account={r.account} />
                        <span className="font-mono text-[13px] font-medium">{r.asset}</span>
                        {/* Монета, а не деньги: в итог по бирже не входит —
                            курса у панели нет, и складывать её с USDT нельзя. */}
                        {!isStable(r.asset) && (
                          <span className="text-[10px] uppercase text-muted-foreground" title={t('coinTitle')}>{t('coin')}</span>
                        )}
                        <span className="ml-auto whitespace-nowrap font-mono text-[14px] tabular-nums">{amount(total, r.asset)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground" title={t('cols.freeShareTitle')}>
                        <div className="bal-free flex-1"><i style={{ width: `${freePct ?? 0}%` }} /></div>
                        <span className="whitespace-nowrap font-mono tabular-nums">
                          {t('freeShort', { amount: amount(avail, r.asset) })}{freePct != null ? ` · ${freePct.toFixed(0)}%` : ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BalancesPanel;
